import { useRouter } from 'next/router'
import { useEffect, useRef } from 'react'

import { getUnlockedScrollY } from '@src/quince-components/src/atoms/modal/hooks/useLockBodyScroll'
import searchCacheStateManager from '@src/quince-site-common/src/redux/globalStateManager/SearchCacheStateManager'
import { shouldUseDesignSystem } from '@src/utils/should-use-design-system'

import { getSearchCacheKey } from '../utils'

const useSearchCache = (searchParams: URLSearchParams) => {
  // cacheKey is the key for the search cache
  const cacheKey = getSearchCacheKey(searchParams)

  const router = useRouter()

  const cacheData = searchCacheStateManager.getSearchCache(cacheKey)

  // currentCacheKey is the key for the current search cache
  const currentCacheKey = useRef(cacheKey)
  // currentCacheKey.current = cacheKey

  // scrollY is the scroll position of the page
  const scrollY = useRef(0)

  // Under DS the anchor engine (`@src/scroll-restoration/use-back-nav-scroll-restoration`, armed in
  // useSearchResults) owns placement on Back, so this pixel restore stands down — two restorers would
  // fight over the viewport. Read at render time; the DS decision is per-page and can flip between
  // client navigations. The save half below is deliberately left armed for both buckets.
  // [STRFRNT-16138]
  const isDesignSystem = shouldUseDesignSystem()

  useEffect(() => {
    // update the current cache key when the cache key changes
    currentCacheKey.current = cacheKey
  }, [cacheKey])

  // Restore scroll position when search params change
  useEffect(() => {
    if (isDesignSystem) {
      return undefined
    }

    if (cacheData?.scrollY) {
      // Use a MutationObserver to detect when content is added to the DOM
      const observer = new MutationObserver(mutations => {
        const hasContentChanges = mutations.some(
          mutation => mutation.type === 'childList' && mutation.addedNodes.length > 0,
        )

        if (hasContentChanges) {
          window.scrollTo(0, cacheData.scrollY)
          observer.disconnect()
        }
      })

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      })

      return () => {
        observer.disconnect()
      }
    }
    return undefined
  }, [searchParams, isDesignSystem])

  useEffect(() => {
    // Store the scroll position of the page
    const handleScroll = () => {
      // Read through getUnlockedScrollY: opening a body-locking modal (Quick Add) pins <body> with
      // position:fixed, which fires a scroll event reporting 0 and would overwrite the real position
      // here. The navigation that follows then saves 0 and Back lands at the top. [STRFRNT-15288]
      scrollY.current = getUnlockedScrollY()
    }

    // Store the scroll position of the page in the search cache when the route changes. Read the
    // manager directly rather than closing over `cacheData` — that's a render-time snapshot and can
    // still be stale (null) here even though a cache entry was seeded moments earlier.
    const handleRouteChange = () => {
      if (searchCacheStateManager.getSearchCache(currentCacheKey.current)) {
        searchCacheStateManager.publishSearchCache(currentCacheKey.current, scrollY.current)
      }
    }

    // Add the event listener for the scroll event to store the scroll position
    window.addEventListener('scroll', handleScroll, { passive: true })
    // Add the event listener for the route change event to store the scroll position in the search cache
    router.events.on('routeChangeStart', handleRouteChange)

    return () => {
      // Remove the event listener for the scroll event
      window.removeEventListener('scroll', handleScroll)
      // Remove the event listener for the route change event
      router.events.off('routeChangeStart', handleRouteChange)
    }
  }, [router.events])

  return {
    currentCacheKey,
    scrollY,
    cacheData,
  }
}

export default useSearchCache
